import type { GameEvent } from '../game/types';

export class AudioSystem {
  muted = false;
  private ctx: AudioContext | null = null;

  resume(): Promise<void> {
    if (!this.ctx || this.ctx.state === 'closed') this.ctx = createAudioContext();
    return this.ctx?.resume()?.catch(() => undefined) ?? Promise.resolve();
  }

  getState(): string {
    if (!this.ctx) return 'unavailable';
    return String(this.ctx.state);
  }

  handle(event: GameEvent): void {
    if (this.muted) return;
    if (this.ctx && this.ctx.state !== 'running') void this.ctx.resume()?.catch(() => undefined);
    if (event.kind === 'shoot') this.playShoot(event);
    if (event.kind === 'hit') this.playHit(event);
    if (event.kind === 'explode') this.playExplosion(event);
    if (event.kind === 'powerup') {
      this.beep(820, 0.08, 0.04, 'sine');
      window.setTimeout(() => this.beep(1180, 0.08, 0.035, 'sine'), 70);
    }
    if (event.kind === 'level') {
      this.beep(380, 0.12, 0.035, 'sine');
      window.setTimeout(() => this.beep(660, 0.12, 0.035, 'sine'), 110);
    }
    if (event.kind === 'countdown') this.beep(880, 0.1, 0.045, 'square');
    if (event.kind === 'gameOver') this.beep(150, 0.36, 0.06, 'sawtooth');
  }

  previewExplosion(): void {
    if (this.muted) return;
    this.battleExplosion();
  }

  playRankedCelebration(tier: 'ranked' | 'elite' | 'champion'): void {
    if (this.muted) return;
    if (this.ctx && this.ctx.state !== 'running') void this.ctx.resume()?.catch(() => undefined);
    switch (tier) {
      case 'champion':
        this.championFanfare();
        break;
      case 'elite':
        this.eliteFanfare();
        break;
      case 'ranked':
      default:
        this.rankedSting();
        break;
    }
  }

  private playShoot(event: GameEvent): void {
    if (event.shootVariant === 'boss') {
      this.bossCannonShot();
      return;
    }
    this.beep(520, 0.055, 0.035, 'square');
  }

  private playExplosion(event: GameEvent): void {
    if ((event.amount ?? 1) >= 2) {
      this.bossExplosion();
      return;
    }
    this.battleExplosion();
  }

  private playHit(event: GameEvent): void {
    switch (event.hitVariant) {
      case 'shield':
        this.shieldPing();
        break;
      case 'base':
        this.baseAlarmHit();
        break;
      case 'armor':
        this.armorClink();
        break;
      case 'impact':
        this.beep(150, 0.07, 0.035, 'triangle');
        break;
      case 'terrain':
      default:
        this.beep(110, 0.085, 0.04, 'triangle');
        break;
    }
  }

  private beep(freq: number, duration: number, volume: number, type: OscillatorType): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(30, freq * 0.42), t + duration);
    gain.gain.setValueAtTime(volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(t);
    osc.stop(t + duration);
  }

  private battleExplosion(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.noiseBurst(t, 0.54, 0.22, 'lowpass', 620);
    this.noiseBurst(t + 0.012, 0.2, 0.09, 'bandpass', 980);
    this.noiseBurst(t + 0.024, 0.18, 0.065, 'highpass', 1800);
    this.sweptTone(52, 24, 0.52, 0.16, 'sawtooth', t);
    this.sweptTone(88, 42, 0.32, 0.075, 'triangle', t + 0.028);
    window.setTimeout(() => this.beep(118, 0.11, 0.04, 'square'), 54);
    window.setTimeout(() => this.beep(208, 0.08, 0.026, 'triangle'), 112);
  }

  private bossExplosion(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.noiseBurst(t, 0.78, 0.3, 'lowpass', 520);
    this.noiseBurst(t + 0.016, 0.34, 0.12, 'bandpass', 860);
    this.noiseBurst(t + 0.042, 0.28, 0.08, 'highpass', 2100);
    this.sweptTone(46, 19, 0.72, 0.2, 'sawtooth', t);
    this.sweptTone(74, 28, 0.48, 0.1, 'triangle', t + 0.032);
    this.sweptTone(132, 64, 0.24, 0.05, 'square', t + 0.09);
    window.setTimeout(() => this.beep(102, 0.15, 0.05, 'square'), 72);
    window.setTimeout(() => this.beep(184, 0.12, 0.03, 'triangle'), 168);
  }

  private bossCannonShot(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.sweptTone(180, 74, 0.16, 0.055, 'sawtooth', t);
    this.noiseBurst(t, 0.065, 0.034, 'bandpass', 780);
    this.sweptTone(420, 190, 0.11, 0.03, 'square', t + 0.018);
  }

  private armorClink(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.sweptTone(220, 160, 0.05, 0.02, 'triangle', t);
    this.sweptTone(760, 620, 0.065, 0.022, 'square', t + 0.01);
    this.sweptTone(1320, 980, 0.082, 0.024, 'triangle', t + 0.02);
  }

  private shieldPing(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.sweptTone(760, 940, 0.05, 0.028, 'sine', t);
    this.sweptTone(1180, 1520, 0.06, 0.022, 'triangle', t + 0.014);
  }

  private baseAlarmHit(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.sweptTone(172, 124, 0.12, 0.04, 'triangle', t);
    this.sweptTone(640, 520, 0.08, 0.026, 'square', t + 0.014);
    this.sweptTone(860, 720, 0.09, 0.02, 'sine', t + 0.05);
  }

  private rankedSting(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.sweptTone(520, 690, 0.12, 0.05, 'triangle', t);
    this.sweptTone(780, 980, 0.11, 0.04, 'sine', t + 0.055);
    this.sweptTone(980, 1280, 0.15, 0.032, 'triangle', t + 0.125);
  }

  private eliteFanfare(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.sweptTone(420, 610, 0.16, 0.07, 'triangle', t);
    this.sweptTone(630, 860, 0.18, 0.05, 'square', t + 0.07);
    this.sweptTone(860, 1260, 0.24, 0.05, 'triangle', t + 0.145);
    this.sweptTone(1280, 1680, 0.2, 0.04, 'sine', t + 0.22);
  }

  private championFanfare(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.sweptTone(280, 420, 0.22, 0.085, 'sawtooth', t);
    this.sweptTone(520, 760, 0.18, 0.065, 'triangle', t + 0.07);
    this.sweptTone(760, 1120, 0.24, 0.055, 'square', t + 0.145);
    this.sweptTone(1120, 1640, 0.28, 0.05, 'triangle', t + 0.22);
    this.sweptTone(1640, 1980, 0.18, 0.04, 'sine', t + 0.285);
    this.noiseBurst(t + 0.26, 0.045, 0.018, 'highpass', 2200);
  }

  private sweptTone(startFreq: number, endFreq: number, duration: number, volume: number, type: OscillatorType, startTime: number): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(startFreq, startTime);
    osc.frequency.exponentialRampToValueAtTime(endFreq, startTime + duration);
    gain.gain.setValueAtTime(volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration);
  }

  private noiseBurst(startTime: number, duration: number, volume: number, filterType: BiquadFilterType, frequency: number): void {
    if (!this.ctx) return;
    const sampleRate = this.ctx.sampleRate;
    const buffer = this.ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i += 1) {
      last = last * 0.72 + (Math.random() * 2 - 1) * 0.28;
      data[i] = last;
    }
    const source = this.ctx.createBufferSource();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();
    source.buffer = buffer;
    filter.type = filterType;
    filter.frequency.setValueAtTime(frequency, startTime);
    gain.gain.setValueAtTime(volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.ctx.destination);
    source.start(startTime);
    source.stop(startTime + duration);
  }
}

function createAudioContext(): AudioContext | null {
  const ctor = getAudioContextCtor();
  if (!ctor) return null;
  try {
    return new ctor();
  } catch {
    return null;
  }
}

function getAudioContextCtor(): (new () => AudioContext) | null {
  if (typeof window === 'undefined') return null;
  const candidate = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: new () => AudioContext }).webkitAudioContext;
  return candidate ?? null;
}
